import { API_BASE_URL, authenticatedFetch } from '../config/api';

export type VideoJobStatus = 'queued' | 'pending' | 'processing' | 'running' | 'completed' | 'failed';
export type VideoQualityPreset = 'balanced' | 'quality' | 'speed';
export type FashionMotionSourceType = 'full_body' | 'catalog';
export type FashionMotionQualityPreset = 'draft' | 'balanced' | 'quality' | 'premium';
export type FashionMotionScenarioPreset = 'runway' | 'editorial' | 'ecommerce' | 'lifestyle';
export type FashionMotionAspectRatio = '9:16' | '1:1' | '16:9';

export interface CreateImageToVideoJobInput {
  image?: string;
  imageUrl?: string;
  prompt: string;
  negativePrompt?: string;
  qualityPreset?: VideoQualityPreset;
  width?: number;
  height?: number;
}

export interface CreateImageToVideoJobResult {
  jobId: string;
}

export interface CreateFashionMotionJobInput {
  image?: string;
  imageUrl?: string;
  sourceType: FashionMotionSourceType;
  qualityPreset?: FashionMotionQualityPreset;
  duration?: number;
  scenarioPreset?: FashionMotionScenarioPreset;
  aspectRatio?: FashionMotionAspectRatio;
  prompt?: string;
  negativePrompt?: string;
  strictProductLock?: boolean;
  seed?: number;
}

export interface CreateFashionMotionJobResult {
  jobId: string;
  status: string;
  job?: FashionMotionJobResult;
}

export interface FashionMotionJobResult {
  jobId: string;
  status: string;
  stage?: string;
  provider?: string;
  model?: string;
  sourceType: FashionMotionSourceType;
  qualityPreset: FashionMotionQualityPreset;
  duration: number;
  aspectRatio: FashionMotionAspectRatio;
  scenarioPreset: FashionMotionScenarioPreset;
  strictProductLock: boolean;
  normalizedImageUrl?: string;
  videoUrl?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VideoJobResult {
  status: VideoJobStatus | string;
  videoUrl?: string;
  error?: string;
}

export interface PollVideoJobOptions {
  intervalMs?: number;
  timeoutMs?: number;
  onTick?: (state: VideoJobResult) => void;
}

export interface PollFashionMotionJobOptions {
  intervalMs?: number;
  timeoutMs?: number;
  onTick?: (state: FashionMotionJobResult) => void;
}

const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000;

const sleep = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

export class ProductMediaService {
  static async createImageToVideoJob(input: CreateImageToVideoJobInput): Promise<CreateImageToVideoJobResult> {
    const response = await authenticatedFetch(`${API_BASE_URL}/media/video/jobs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: input.image,
        imageUrl: input.imageUrl,
        prompt: input.prompt,
        negativePrompt: input.negativePrompt,
        qualityPreset: input.qualityPreset || 'balanced',
        width: input.width ?? 480,
        height: input.height ?? 832,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data?.jobId) {
      throw new Error(data?.error || data?.message || 'Video job baslatilamadi');
    }

    return {
      jobId: data.jobId,
    };
  }

  static async getVideoJob(jobId: string): Promise<VideoJobResult> {
    const response = await authenticatedFetch(`${API_BASE_URL}/media/video/jobs/${jobId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || data?.message || 'Video job durumu alinamadi');
    }

    return {
      status: data.status || 'processing',
      videoUrl: data.videoUrl,
      error: data.error,
    };
  }

  static async createFashionMotionJob(input: CreateFashionMotionJobInput): Promise<CreateFashionMotionJobResult> {
    const response = await authenticatedFetch(`${API_BASE_URL}/media/fashion-motion/jobs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: input.image,
        imageUrl: input.imageUrl,
        sourceType: input.sourceType,
        qualityPreset: input.qualityPreset || 'balanced',
        duration: input.duration ?? 6,
        scenarioPreset: input.scenarioPreset || 'editorial',
        aspectRatio: input.aspectRatio || '9:16',
        prompt: input.prompt,
        negativePrompt: input.negativePrompt,
        strictProductLock: input.strictProductLock !== false,
        seed: input.seed,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data?.jobId) {
      throw new Error(data?.error || data?.message || 'Fashion motion job baslatilamadi');
    }

    return {
      jobId: data.jobId,
      status: data.status || 'queued',
      job: data.job,
    };
  }

  static async getFashionMotionJob(jobId: string): Promise<FashionMotionJobResult> {
    const response = await authenticatedFetch(`${API_BASE_URL}/media/fashion-motion/jobs/${jobId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || data?.message || 'Fashion motion job durumu alinamadi');
    }

    return data as FashionMotionJobResult;
  }

  static async pollVideoJob(jobId: string, options: PollVideoJobOptions = {}): Promise<VideoJobResult> {

    const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const startTime = Date.now();

    while (Date.now() - startTime <= timeoutMs) {
      const state = await ProductMediaService.getVideoJob(jobId);
      options.onTick?.(state);

      if (state.status === 'completed' || state.status === 'failed') {
        return state;
      }

      await sleep(intervalMs);
    }

    throw new Error('Video olusturma zaman asimina ugradi. Lutfen tekrar deneyin.');
  }

  static async pollFashionMotionJob(jobId: string, options: PollFashionMotionJobOptions = {}): Promise<FashionMotionJobResult> {
    const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const startTime = Date.now();

    while (Date.now() - startTime <= timeoutMs) {
      const state = await ProductMediaService.getFashionMotionJob(jobId);
      options.onTick?.(state);

      if (state.status === 'completed' || state.status === 'failed') {
        return state;
      }

      await sleep(intervalMs);
    }

    throw new Error('Fashion motion olusturma zaman asimina ugradi. Lutfen tekrar deneyin.');
  }
}
